import {
  DEFAULT_NODE_CIRCLE_RADIUS,
  DEFAULT_NODE_CIRCLE_STROKE_WIDTH
} from "../components/map/defines";
import { getOrientationLayout } from "../components/map/shapes/SpotShape/helpers";
import { GroupShape, Shape, SpotShape, ZoneShape } from "../types";
import { getEffectiveSpotAppearance } from "./spots";
import {
  computeZoneAreaPx,
  sampleZonePolygonPoints,
  ZonePolygonPoint
} from "./zonePolygon";

export const isPointInRect = (
  px: number,
  py: number,
  rx: number,
  ry: number,
  rw: number,
  rh: number
): boolean => {
  return px >= rx && px <= rx + rw && py >= ry && py <= ry + rh;
};

/** Zone ids look like `zone_<uuid>`; map-local categories never do. */
export const isOrganisationZoneId = (id: string | undefined): boolean =>
  !!id && id.startsWith("zone_");

/** Zone shapes that describe a space; link-only zones are not regions. */
export const isRegionZone = (shape: Shape): shape is ZoneShape =>
  shape.type === "zone" && (shape as ZoneShape).zoneType !== "clickable";

export interface PlacedSpot {
  id: string;
  /** Centre of the spot's box, what containment tests. */
  centerX: number;
  centerY: number;
  /** Centre and outer radius of the availability dot, what a marker
   *  around the spot should ring. */
  dotX: number;
  dotY: number;
  dotRadius: number;
  /** The highlight area stands in for the dot ("show dot" off). */
  hidesDot: boolean;
  /** Canvas-space box around the highlight area, when one is configured. */
  areaBounds?: { x: number; y: number; width: number; height: number };
}

/** World-space box around the spot's highlight area, following the
 *  spot's rotation about its origin. */
const areaBoundsOf = (
  spot: SpotShape,
  layout: ReturnType<typeof getOrientationLayout>,
  offsetX: number,
  offsetY: number
): PlacedSpot["areaBounds"] => {
  const area = spot.stateArea;
  if (!area) return undefined;
  let corners: { x: number; y: number }[] = [];
  if (area.kind === "polygon") {
    corners = (area.points ?? []) as { x: number; y: number }[];
  } else {
    const rect =
      area.kind === "spot-rect"
        ? layout.rect.width && layout.rect.height
          ? layout.rect
          : null
        : area.rect?.width && area.rect?.height
          ? area.rect
          : null;
    if (!rect) return undefined;
    corners = [
      { x: rect.x, y: rect.y },
      { x: rect.x + rect.width, y: rect.y },
      { x: rect.x + rect.width, y: rect.y + rect.height },
      { x: rect.x, y: rect.y + rect.height }
    ];
  }
  if (corners.length < 3) return undefined;
  const radians = ((spot.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const world = corners.map((corner) => ({
    x: offsetX + spot.x + corner.x * cos - corner.y * sin,
    y: offsetY + spot.y + corner.x * sin + corner.y * cos
  }));
  const xs = world.map((point) => point.x);
  const ys = world.map((point) => point.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return {
    x: minX,
    y: minY,
    width: Math.max(...xs) - minX,
    height: Math.max(...ys) - minY
  };
};

/** The dot's centre in canvas coordinates, following the spot's rotation
 *  about its origin, and its radius including the stroke. */
const layoutOf = (spot: SpotShape) =>
  getOrientationLayout(
    getEffectiveSpotAppearance(spot.spotType, spot.spotAppearance),
    spot.orientation,
    spot.labelPlacement,
    spot.labelOrientation || "auto",
    0,
    0,
    spot.spacing || 0,
    spot.rowSpacing || 0,
    spot.width,
    spot.height
  );

const dotOf = (
  spot: SpotShape,
  offsetX: number,
  offsetY: number
): { x: number; y: number; radius: number } => {
  const layout = getOrientationLayout(
    getEffectiveSpotAppearance(spot.spotType, spot.spotAppearance),
    spot.orientation,
    spot.labelPlacement,
    spot.labelOrientation || "auto",
    0,
    0,
    spot.spacing || 0,
    spot.rowSpacing || 0,
    spot.width,
    spot.height
  );
  const radians = ((spot.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return {
    x: offsetX + spot.x + layout.circle.x * cos - layout.circle.y * sin,
    y: offsetY + spot.y + layout.circle.x * sin + layout.circle.y * cos,
    radius:
      (spot.circleProps?.radius || DEFAULT_NODE_CIRCLE_RADIUS) +
      (spot.circleProps?.strokeWidth || DEFAULT_NODE_CIRCLE_STROKE_WIDTH) / 2
  };
};

// Spots inside groups are stored relative to the group, so their centres
// need the group offsets accumulated on the way down.
export const collectSpots = (
  shapes: Shape[],
  offsetX = 0,
  offsetY = 0
): PlacedSpot[] => {
  return shapes.flatMap((s) => {
    if (s.type === "spot") {
      const spot = s as SpotShape;
      const dot = dotOf(spot, offsetX, offsetY);
      return [
        {
          id: spot.id,
          centerX: offsetX + spot.x + spot.width / 2,
          centerY: offsetY + spot.y + spot.height / 2,
          dotX: dot.x,
          dotY: dot.y,
          dotRadius: dot.radius,
          hidesDot: spot.stateArea?.hideDot === true,
          areaBounds: areaBoundsOf(spot, layoutOf(spot), offsetX, offsetY)
        }
      ];
    }
    if (s.type === "group") {
      const group = s as GroupShape;
      return collectSpots(group.children, offsetX + group.x, offsetY + group.y);
    }
    return [];
  });
};

/**
 * Outline of a zone in canvas coordinates: the sampled polygon (curves
 * flattened) or the four rectangle corners.
 */
export const zoneOutline = (zone: ZoneShape): ZonePolygonPoint[] => {
  if ((zone.vertices?.length ?? 0) >= 3) {
    return sampleZonePolygonPoints(
      zone.vertices as ZonePolygonPoint[],
      zone.edges
    ).map((point) => ({ x: zone.x + point.x, y: zone.y + point.y }));
  }
  return [
    { x: zone.x, y: zone.y },
    { x: zone.x + zone.width, y: zone.y },
    { x: zone.x + zone.width, y: zone.y + zone.height },
    { x: zone.x, y: zone.y + zone.height }
  ];
};

/** Ray casting; points on an edge count as inside. */
export const isPointInPolygon = (
  px: number,
  py: number,
  polygon: ZonePolygonPoint[]
): boolean => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i];
    const b = polygon[j];
    if (isPointOnSegment(px, py, a, b)) return true;
    const crosses =
      a.y > py !== b.y > py &&
      px < ((b.x - a.x) * (py - a.y)) / (b.y - a.y) + a.x;
    if (crosses) inside = !inside;
  }
  return inside;
};

const EPSILON = 1e-6;

const isPointOnSegment = (
  px: number,
  py: number,
  a: ZonePolygonPoint,
  b: ZonePolygonPoint
): boolean => {
  const cross = (px - a.x) * (b.y - a.y) - (py - a.y) * (b.x - a.x);
  if (Math.abs(cross) > EPSILON) return false;
  return (
    px >= Math.min(a.x, b.x) - EPSILON &&
    px <= Math.max(a.x, b.x) + EPSILON &&
    py >= Math.min(a.y, b.y) - EPSILON &&
    py <= Math.max(a.y, b.y) + EPSILON
  );
};

export const isPointInZone = (
  px: number,
  py: number,
  zone: ZoneShape
): boolean => {
  if ((zone.vertices?.length ?? 0) < 3) {
    return isPointInRect(px, py, zone.x, zone.y, zone.width, zone.height);
  }
  if (!isPointInRect(px, py, zone.x, zone.y, zone.width, zone.height)) {
    return false;
  }
  return isPointInPolygon(px, py, zoneOutline(zone));
};

// Outlines and areas are computed once per call of prepareRegionsForSave:
// the parent search compares every region with every other, and sampling a
// curved polygon each time made large maps crawl while dragging.
interface RegionGeometry {
  zone: ZoneShape;
  /** Canvas-space box: the zone's own box plus its group offsets. */
  bounds: { x: number; y: number; width: number; height: number };
  outline: ZonePolygonPoint[];
  areaPx: number;
  isPolygon: boolean;
}

/** A region zone with the group offsets that put it on the canvas. Zones
 *  inside a group are stored relative to it, like spots. */
export interface PlacedRegion {
  zone: ZoneShape;
  offsetX: number;
  offsetY: number;
}

/** Region zones at any depth, groups included, with their offsets. */
export const collectRegionZones = (
  shapes: Shape[],
  offsetX = 0,
  offsetY = 0
): PlacedRegion[] =>
  shapes.flatMap((shape) => {
    if (isRegionZone(shape)) return [{ zone: shape, offsetX, offsetY }];
    if (shape.type === "group") {
      const group = shape as GroupShape;
      return collectRegionZones(
        group.children,
        offsetX + group.x,
        offsetY + group.y
      );
    }
    return [];
  });

/** Region zones at any depth with canvas-space positions, for anything
 *  that draws or lists them (checks, overlays). */
export const absoluteRegionZones = (shapes: Shape[]): ZoneShape[] =>
  collectRegionZones(shapes).map(({ zone, offsetX, offsetY }) =>
    offsetX === 0 && offsetY === 0
      ? zone
      : { ...zone, x: zone.x + offsetX, y: zone.y + offsetY }
  );

const geometryOf = ({
  zone,
  offsetX,
  offsetY
}: PlacedRegion): RegionGeometry => ({
  zone,
  bounds: {
    x: zone.x + offsetX,
    y: zone.y + offsetY,
    width: zone.width,
    height: zone.height
  },
  outline: zoneOutline(zone).map((point) => ({
    x: point.x + offsetX,
    y: point.y + offsetY
  })),
  areaPx: computeZoneAreaPx(zone),
  isPolygon: (zone.vertices?.length ?? 0) >= 3
});

const containsPoint = (
  geometry: RegionGeometry,
  px: number,
  py: number
): boolean => {
  const { bounds } = geometry;
  if (!isPointInRect(px, py, bounds.x, bounds.y, bounds.width, bounds.height)) {
    return false;
  }
  return geometry.isPolygon ? isPointInPolygon(px, py, geometry.outline) : true;
};

const isInside = (inner: RegionGeometry, outer: RegionGeometry): boolean => {
  const a = inner.bounds;
  const b = outer.bounds;
  if (
    a.x < b.x ||
    a.y < b.y ||
    a.x + a.width > b.x + b.width ||
    a.y + a.height > b.y + b.height
  ) {
    return false;
  }
  return inner.outline.every((point) => containsPoint(outer, point.x, point.y));
};

/**
 * Fills in what the server validates and stores for every region on save:
 * which spots sit inside it (polygon-aware, spot centre), its area in
 * canvas pixels, and the smallest other region it is drawn inside. Link
 * zones are left untouched. Returns a new shapes array; unchanged shapes
 * keep their identity.
 */
export const prepareRegionsForSave = (shapes: Shape[]): Shape[] => {
  const regions = collectRegionZones(shapes);
  if (regions.length === 0) return shapes;

  const spots = collectSpots(shapes);
  const geometries = regions.map(geometryOf);
  // Smallest first, so the first container found is the tightest.
  const bySize = [...geometries].sort((a, b) => a.areaPx - b.areaPx);
  const geometryById = new Map(geometries.map((g) => [g.zone.id, g]));

  const prepare = (shape: ZoneShape): ZoneShape => {
    const geometry = geometryById.get(shape.id) as RegionGeometry;

    const containedSpotIds = spots
      .filter((spot) => containsPoint(geometry, spot.centerX, spot.centerY))
      .map((spot) => spot.id);

    const parent = bySize.find(
      (candidate) =>
        candidate.zone.id !== shape.id &&
        candidate.areaPx > geometry.areaPx &&
        isInside(geometry, candidate)
    );

    return {
      ...shape,
      containedSpotIds,
      areaPx: geometry.areaPx,
      parentShapeId: parent?.zone.id
    };
  };

  // Regions inside groups are prepared in place; a group only gets a new
  // identity when one of its descendants did.
  const prepareList = (list: Shape[]): Shape[] => {
    let changed = false;
    const next = list.map((shape) => {
      if (isRegionZone(shape)) {
        changed = true;
        return prepare(shape);
      }
      if (shape.type === "group") {
        const group = shape as GroupShape;
        const children = prepareList(group.children);
        if (children !== group.children) {
          changed = true;
          return { ...group, children };
        }
      }
      return shape;
    });
    return changed ? next : list;
  };

  return prepareList(shapes);
};

/** An untagged room or corridor is a physical space; anything tagged with
 *  an organisation zone, or drawn as an open area, is an allocation. */
const isPhysicalRegion = (zone: ZoneShape): boolean =>
  !zone.zone_id && (zone.zoneType === "room" || zone.zoneType === "corridor");

/**
 * Regions the server will record as a conflict: physical spaces (untagged
 * rooms and corridors) containing the same spot without one nesting the
 * other, or tagged regions doing the same, so the spot's zone would be
 * ambiguous. A room straddling a zone's edge is fine: the room is the
 * space, the zone area gives the zone.
 */
export const findRegionConflicts = (
  shapes: Shape[]
): { spotId: string; zoneIds: string[] }[] => {
  const zones = collectRegionZones(shapes).map((region) => region.zone);
  const parentOf = new Map<string, string | undefined>();
  for (const zone of zones) parentOf.set(zone.id, zone.parentShapeId);

  const isAncestor = (ancestorId: string, zoneId: string): boolean => {
    let current = parentOf.get(zoneId);
    for (let guard = 0; current && guard <= zones.length; guard++) {
      if (current === ancestorId) return true;
      current = parentOf.get(current);
    }
    return false;
  };
  const isChain = (group: ZoneShape[]): boolean =>
    group.every((zone) =>
      group.every(
        (other) =>
          other.id === zone.id ||
          isAncestor(other.id, zone.id) ||
          isAncestor(zone.id, other.id)
      )
    );

  const zonesBySpot = new Map<string, ZoneShape[]>();
  for (const zone of zones) {
    for (const spotId of zone.containedSpotIds ?? []) {
      zonesBySpot.set(spotId, [...(zonesBySpot.get(spotId) ?? []), zone]);
    }
  }

  const conflicts: { spotId: string; zoneIds: string[] }[] = [];
  for (const [spotId, containing] of zonesBySpot) {
    const physical = containing.filter(isPhysicalRegion);
    const spaceCandidates =
      physical.length > 0
        ? physical
        : containing.filter((zone) => !isPhysicalRegion(zone));
    const tagged = containing.filter((zone) => !!zone.zone_id);
    for (const group of [spaceCandidates, tagged]) {
      if (group.length >= 2 && !isChain(group)) {
        conflicts.push({ spotId, zoneIds: group.map((zone) => zone.id) });
      }
    }
  }
  return conflicts;
};

/** Computes which zones are adjacent (share a boundary within threshold) */
export const computeZoneAdjacency = (
  shapes: Shape[]
): Map<string, string[]> => {
  const zones = shapes.filter((s): s is ZoneShape => s.type === "zone");
  const adjacency = new Map<string, string[]>();
  const threshold = 30;

  for (const zone of zones) {
    adjacency.set(zone.id, []);
  }

  for (let i = 0; i < zones.length; i++) {
    for (let j = i + 1; j < zones.length; j++) {
      const a = zones[i];
      const b = zones[j];
      if (areZonesAdjacent(a, b, threshold)) {
        adjacency.get(a.id)!.push(b.id);
        adjacency.get(b.id)!.push(a.id);
      }
    }
  }

  return adjacency;
};

const areZonesAdjacent = (
  a: ZoneShape,
  b: ZoneShape,
  threshold: number
): boolean => {
  const aRight = a.x + a.width;
  const bRight = b.x + b.width;
  const aBottom = a.y + a.height;
  const bBottom = b.y + b.height;

  const horizontalOverlap = a.x < bRight && aRight > b.x;
  const verticalOverlap = a.y < bBottom && aBottom > b.y;

  const horizontallyAdjacent =
    Math.abs(aRight - b.x) < threshold || Math.abs(bRight - a.x) < threshold;
  const verticallyAdjacent =
    Math.abs(aBottom - b.y) < threshold || Math.abs(bBottom - a.y) < threshold;

  return (
    (horizontalOverlap && verticallyAdjacent) ||
    (verticalOverlap && horizontallyAdjacent)
  );
};
